import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import ReactLoading from 'react-loading';
import { Button, Col, Form, FormGroup, Input, Label, Row, UncontrolledTooltip } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DatePicker from 'react-datepicker';
import axios from 'axios';
import { get } from 'lodash-es';
import moment from 'moment-timezone';

import AddIcon from '@mui/icons-material/Add';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import EditLocation from '../../Locations/EditLocation';
import ShippingLocationModal from './ShippingLocationModal';
import CustomInputField from '../../../utilities/form-components/CustomInputField';
import InlineAlert from '../../../utilities/form-components/InlineAlert';
import formatDate from '../diy-utils/formatDate';
import { timeAMPM, timeHours, timeMinutes } from '../diy-utils/timeMap';
import { shippingApi } from '../../../api/shipping';

const minDate = new Date();
minDate.setDate(new Date().getDate() + 1);
const maxDate = new Date();
maxDate.setFullYear(new Date().getFullYear() + 2);

// This is formatted to two lines as per UX
const dateTooltipTextLine1 = 'We require a separate session for each day of an auction that is running multiple days. To add more sessions, find the sale on the auction calendar page and click ';
const dateTooltipTextLine2 = '"+ Add Session".';
const timeZoneTooltipText = 'Timezone is based on your session location.';
const liveToTimedTooltipText = 'If there are 15 or more unsold items at the end of your sale, we will automatically create a timed catalog. You will be able to review and edit the sale before you publish it.';

const errorCatalogLimitExceededText = 'You have reached the maximum number of sessions allowed per sale. In order to create a new session, please first add a new sale.';
const errorText = `Unable to create a session. Please try again later or contact Customer
Care any time at:`;

const popoverTermsText = 'Select the location where your items will ship from. You may choose from your saved locations from your settings or add a new address here. If you add new lots, we will default to this shipping location.';

class Step1 extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      formErrors: {},
      formValues: {
        title: '',
        location: '',
        date: null,
        dateString: '',
        hour: '',
        minute: '',
        ampm: '',
        timeZone: '',
        defaultShippingOriginID: '',
        isShippingEnabled: false
      },
      showModal: false,
      showShippingModal: false,
      isShippingModalOpen: false,
      newShippingLocationName: '',
      showCatalogLimitExceededError: false,
      showSubmitError: false,
      timedStaggerChecked: true,
      timeZoneEnabled: false,
      timedValue: 60,
    };
  }

  /**
   * If we have a catalogID in our URL - we are in an "Edit Session" phase and need to
   * pre-fill data from the summary endpoint.
   *
   * If the catalogID is 0, we are in an "Add Session" phase and need to do everything
   * from scratch.
   */
  async componentDidMount() {
    const catalogID = get(this.props, 'match.params.catalogID', '0');
    const { house: { houseID } } = this.props;

    if (catalogID !== '0') {
      // Edit Session case - load summary API and restore user selected options.
      this.fetchSummary(catalogID);
    } else {
      // Add Session case, we only need to fetch locations.
      // fetchLocations internally calls fetchShippingOrgins when shipping is enabled.
      this.fetchLocations();
    }
  }

  async componentDidUpdate(prevProps, prevState) {
    if (prevState.newShippingLocationName !== this.state.newShippingLocationName) {
      const newShippingLocation = this.state.shippingLocations.origins.filter(
        origin => origin.name === this.state.newShippingLocationName
      );
      if (newShippingLocation.length > 0) {
        this.setState({
          formValues: { defaultShippingOriginID: newShippingLocation[0].id },
        });
      }
    }
  }

  fetchSummary = catalogID => {
    axios
      .get(`/cps/app/catalog/${catalogID}`)
      .then(response => {
        const summary = get(response, 'data.data.sessionDefinition', null);
        // fetchLocations internally calls fetchShippingOrgins when shipping is enabled.
        this.fetchLocations(summary);
      })
      .catch(() => {
        // Could not find the catalog id, treat it as new
        this.fetchLocations(null);
      });
  };

  fetchLocations = async (summaryDetails = null) => {
    const {
      house: { houseID },
      match: {
        params: { groupID }
      }
    } = this.props;

    try {
      const [addressResponse, houseResponse, groupResponse] = await Promise.all([
        axios.get('/app/house/address'),
        axios.get(`/cps/app/house/${houseID}/info`),
        axios.get(`/cps/app/group/${groupID}/info`)
      ]);


      const { canAddCatalogs, isTimed } = groupResponse.data.data.groupInfo;
      const { liveToTimedEligible } = houseResponse.data.data.houseInfo;
      const { houseAddresses } = addressResponse.data.data;

      if (summaryDetails) {
        // This is the edit session case, pre populate the fields
        const {
          addressID,
          ampm,
          audioVideo,
          date,
          description,
          hour,
          minute,
          resellUnsoldLiveLots,
          timedType,
          timedValue,
          TZ_zoneName: timeZoneName,
          title,
          viewNotes
        } = summaryDetails;

        const year = moment(date, 'MM/DD/YYYY').year();
        const month = moment(date, 'MM/DD/YYYY').month();
        const timeZone = moment.tz([year, month], timeZoneName).zoneAbbr();

        const selectedLocation = houseAddresses.findIndex(
          location => location.addressID === addressID
        );

        this.setState({
          audioVideo,
          description,
          isTimed,
          liveToTimedEligible,
          loading: false,
          locations: houseAddresses,
          formValues: {
            ampm: ampm || '',
            date: new Date(date) || null,
            dateString: date || '',
            hour: hour || '',
            minute: minute || '',
            location: selectedLocation.toString() || null,
            timeZone: timeZone || '',
            title: title || ''
          },
          originalLttValue: resellUnsoldLiveLots,
          resellUnsoldLiveLots: resellUnsoldLiveLots !== 'N',
          timedStaggerChecked: timedType === 1,
          timedValue,
          timeZoneEnabled: true,
          viewNotes
        });
      } else {
        // This is either the fresh add session case, or we have just added a new location
        const { locations, defaultShippingOriginID } = this.state;
        // Default the input field to the primary location
        let locationIndex = '0';
        const timeZoneID = this.props.house.houseAddress.timeZoneID;
        const date = this.state.formValues.date || new Date();
        let timeZone = this.getTimeZone(timeZoneID, date);

        /**
         * If we already have locations - this means we are re-fetching
         * after the initial page load - aka a user added a new location.
         * Set the input field to the new location.
         */
        if (locations) {
          locationIndex = houseAddresses.length - 1;

          const { date } = this.state.formValues;
          if (date) {
            const { timeZoneID } = houseAddresses[locationIndex];
            timeZone = this.getTimeZone(timeZoneID, date);
          }
        }
        this.setState({
          isTimed,
          liveToTimedEligible,
          loading: false,
          locations: houseAddresses,
          resellUnsoldLiveLots: true,
          formValues: {
            ...this.state.formValues,
            location: locationIndex,
            timeZone
          },
          showCatalogLimitExceededError: !canAddCatalogs
        });
      }
      if(this.state.isShippingEnabled === undefined || this.state.isShippingEnabled === true) {
        // The app bootstrap already fetched the policy and stored it in Redux.
        // Re-use that data to avoid a duplicate /policy call on page load.
        // Exception: when the user just added a new shipping location, or Redux
        // policy hasn't loaded yet, we fall back to the API to avoid treating a
        // missing policy as "shipping disabled" due to a load-order race.
        if (this.state.newShippingLocationName !== '' || !this.props.policy?.id) {
          this.fetchShippingOrgins(summaryDetails);
        } else {
          this.applyShippingFromPolicy(summaryDetails);
        }
      }
    } catch (error) {
      console.log(error);
      this.setState({ loading: false });
    }
  };

  applyShippingFromPolicy = (summaryDetails) => {
    const policy = this.props.policy;

    if (!policy?.id) {
      this.setState({ isShippingEnabled: false });
      return;
    }

    let defaultShippingOriginID;
    if (summaryDetails?.defaultShippingOriginID) {
      const match = policy.origins?.find(o => o.id === summaryDetails.defaultShippingOriginID);
      defaultShippingOriginID = match?.id ?? policy.origins?.[0]?.id;
    } else {
      defaultShippingOriginID = policy.origins?.[0]?.id;
    }

    this.setState(prev => ({
      formValues: {
        ...prev.formValues,
        defaultShippingOriginID,
        isShippingEnabled: true,
      },
      shippingLocations: policy,
    }));
  };

  fetchShippingOrgins = async (summaryDetails) => {
    try {
      const { house: { houseID } } = this.props;
      const accountID = houseID;
      const accountDomain = 'AUCTIONHOUSE';
      const response = await shippingApi.getShippingPolicy(accountID, accountDomain);
      if(response.data) {
        const shippingLocations = get(response, 'data.policy', []);
        if (summaryDetails !== null && summaryDetails !== undefined) {
          if (summaryDetails.defaultShippingOriginID !== null) {
            const currentShippingLocation = shippingLocations.origins.filter(origin => origin.id === summaryDetails.defaultShippingOriginID);
            this.setState((prev) => {
              return {
                formValues:{
                  ...prev.formValues,
                  defaultShippingOriginID: currentShippingLocation?.[0]?.id,
                  isShippingEnabled: true,
                },
                shippingLocations
              }
            });
          } else {
            this.setState((prev) => {
              return {
                formValues:{
                  ...prev.formValues,
                  defaultShippingOriginID: shippingLocations.origins?.[0]?.id,
                  isShippingEnabled: true,
                },
                shippingLocations
              }
            });
          }
        } else {
          if(this.state.newShippingLocationName !== '') {
            const newShippingLocation = shippingLocations.origins.filter(origin => origin.name === this.state.newShippingLocationName);
            this.setState((prev) => {
              return {
                formValues:{
                  ...prev.formValues,
                  defaultShippingOriginID: newShippingLocation?.[0]?.id,
                },
                shippingLocations
              }
            });
          } else {
            this.setState((prev) => {
              return {
                formValues:{
                  ...prev.formValues,
                  defaultShippingOriginID: shippingLocations.origins?.[0]?.id,
                  isShippingEnabled: true,
                },
                shippingLocations
              }
            });
          }
        }
      }
      if(response.error?.code === 'POLICY_NOT_FOUND'){
        this.setState({  isShippingEnabled: false });
      }
    } catch {
      this.setState({ errorLoadingTerms: true });
    }
  };

  /** *********************************************************
   * Start Form Handlers
   ********************************************************** */

  // User changes the session title
  handleTitleChange = e => {
    const formErrors = { ...this.state.formErrors };
    delete formErrors[e.target.name];

    this.setState({
      formErrors,
      formValues: {
        ...this.state.formValues,
        [e.target.name]: e.target.value
      }
    });
  };

  // User selects a location from the dropdown
  handleSelectLocation = e => {
    const formErrors = { ...this.state.formErrors };
    delete formErrors.location;

    const location = e.target.value;
    const { formValues, locations } = this.state;
    const date = get(formValues, 'date', null);

    const { timeZoneID } = locations[location];
    const timeZone = this.getTimeZone(timeZoneID, date);

    this.setState({
      formErrors,
      formValues: {
        ...this.state.formValues,
        location,
        timeZone
      },
      timeZoneEnabled: timeZone != null
    });
  };

  getTimeZone = (timeZoneID, date) => {
    if (!date) {
      return null;
    }

    return moment.tz(date, timeZoneID).format('z');
  };

  handleShippingLocation = event => {
    const formErrors = { ...this.state.formErrors };
    delete formErrors.defaultShippingOriginID;

    const shippingLocations = event.target.value;

    this.setState({
      formErrors,
      formValues: {
        ...this.state.formValues,
        defaultShippingOriginID: shippingLocations,
      }
    });
    this.setState({
      defaultShippingOriginID: event.currentTarget.value
    });
  };

  handleCheckbox = () => {
    this.setState({
      timedStaggerChecked: !this.state.timedStaggerChecked
    });
  };

  // User clicks add new address - show add location modal
  handleLocationModal = showModal => {
    this.setState({ showModal });
  };

  // User clicks Add new Shipping address
  handleShippingModal = (isOpen) => {
    this.setState({ showShippingModal: isOpen });
  };

  handleShippingModalData = (data) => {
      const response = this.fetchShippingOrgins(this.summaryDetails = null);
      this.setState({ newShippingLocationName: data });
  };

  // User changes the session date
  handleSelectDate = date => {
    if (!date) {
      this.setState({
        ...this.state.formErrors,
        formValues: {
          ...this.state.formValues,
          date: null,
          dateString: ''
        },
        timeZoneEnabled: false
      });
    } else {
      const formErrors = { ...this.state.formErrors };
      delete formErrors.date;

      // The calendar wants Date format, but BE wants MM/DD/YYYY
      const dateString = formatDate(date);

      const {
        locations,
        formValues: { location }
      } = this.state;
      const { timeZoneID } = locations[location];

      const timeZone = this.getTimeZone(timeZoneID, date);

      this.setState({
        formErrors,
        formValues: {
          ...this.state.formValues,
          date,
          dateString,
          timeZone
        },
        timeZoneEnabled: true
      });
    }
  };

  // User changes session time - either HH, MM, or AM/PM
  handleSelectTime = (e, option) => {
    const formErrors = { ...this.state.formErrors };
    delete formErrors[option];

    // Option is one of 'hour', 'minute', 'ampm'
    this.setState({
      formErrors,
      formValues: {
        ...this.state.formValues,
        [option]: e.target.value
      }
    });
  };

  handleReSellCheckbox = e => {
    this.setState({ resellUnsoldLiveLots: e.target.checked });
  };

  handleStaggerSelection = e => {
    this.setState({ timedValue: e.currentTarget.value });
  };

  /** *********************************************************
   * End Form Handlers
   ********************************************************** */
  /** *********************************************************
   * Start Validation
   ********************************************************** */
  getErrorMessage = fieldName => {
    const {
      match: {
        params: { type }
      }
    } = this.props;
    switch (fieldName) {
      case 'title':
        return 'Please enter a session title';
      case 'hour':
      case 'minute':
        return type.toLowerCase() === 'timed'
          ? 'Please select first lot end time'
          : 'Please select session time';
      case 'ampm':
        return 'Please select AM/PM';
      default:
        return null;
    }
  };

  validateRequired = e => {
    if (!e.target.value) {
      this.setState({
        ...this.state,
        formErrors: {
          ...this.state.formErrors,
          [e.target.name]: this.getErrorMessage(e.target.name)
        }
      });
    }
  };

  /**
   * A valid date must be between today and 2 years from now.  The DatePicker
   * widget allows for a minDate and maxDate field which blocks those items, but
   * the user could still technically try to enter an invalid date by typing it in.
   */
  validateDate = date => {
    if (date) {
      const { dateString } = this.state.formValues;
      const {
        match: {
          params: { type }
        }
      } = this.props;

      if (!dateString || dateString.length === 0) {
        this.setState({
          ...this.state,
          formErrors: {
            ...this.state.formErrors,
            date:
              type.toLowerCase() === 'timed'
                ? 'Please select a first lot end date'
                : 'Please select a session date'
          },
          formValues: {
            ...this.state.formValues,
            date: null,
            dateString: '',
            timeZone: ''
          },
          timeZoneEnabled: false
        });
      } else {
        // Input Date comes in at 00:00:00
        const inputDate = new Date(dateString);
        const todayDate = new Date();

        // Input Date will be less than today date which will fail validation if
        // the user selects the current day.
        if (inputDate < todayDate || inputDate > maxDate) {
          this.setState({
            ...this.state,
            formErrors: {
              ...this.state.formErrors,
              date: 'Session date must be within the next two years'
            },
            formValues: {
              ...this.state.formValues,
              date: null,
              dateString: '',
              timeZone: ''
            },
            timeZoneEnabled: false
          });
        }
      }
    }
  };

  /** *********************************************************
   * End Validation
   ********************************************************** */
  /** *********************************************************
   * Start Form Submit
   ********************************************************** */
  submitStepOne = async () => {
    const {
      audioVideo,
      formValues: { ampm, dateString, hour, location, defaultShippingOriginID, minute, title },
      description,
      liveToTimedEligible,
      locations,
      originalLttValue,
      resellUnsoldLiveLots,
      timedStaggerChecked,
      timedValue,
      viewNotes
    } = this.state;
    const { addressID } = locations[location];

    const {
      house: { houseID },
      location: { state },
      match: {
        params: { catalogID, groupID, type }
      },
      user: { userName }
    } = this.props;

    const isEditingSession = get(state, 'linkedFrom', null) === 'step6';
    let requestBody;
    if(this.state.isShippingEnabled === false){
      requestBody = {
        creator: userName,
        date: dateString,
        dateEnd: '',
        liveStatus: '1',
        addressID,
        ampm,
        description,
        groupID: parseInt(groupID, 10),
        hour,
        houseID,
        minute,
        resellUnsoldLiveLots: liveToTimedEligible && resellUnsoldLiveLots ? 'Y' : 'N',
        title,
        viewNotes,
      };
    } else {
      requestBody = {
        creator: userName,
        date: dateString,
        dateEnd: '',
        liveStatus: '1',
        addressID,
        defaultShippingOriginID,
        ampm,
        description,
        groupID: parseInt(groupID, 10),
        hour,
        houseID,
        minute,
        resellUnsoldLiveLots: liveToTimedEligible && resellUnsoldLiveLots ? 'Y' : 'N',
        title,
        viewNotes,
      };
    }


    const axiosPostUrl = `/cps/app/catalog/${catalogID}`;
    const shippingPostUrl = '/cps/app/catalog/0';
    const isTimedCatalog = type.toLowerCase() === 'timed';

    if (isTimedCatalog) {
      /**
       * BE is setting this value incorrectly to 'N' even if it doesn't
       * exist, we need to keep it how it was
       */
      requestBody.resellUnsoldLiveLots = originalLttValue;
      requestBody.isTimed = 1;
      requestBody.timedType = timedStaggerChecked ? 1 : 0;
      requestBody.timedValue = timedStaggerChecked ? timedValue : 0;
    } else {
      // FE workaround because BE will reset this to off for some reason
      requestBody.audioVideo = audioVideo;
    }

    // Case for when the user is editing a session from step 6
    if (isEditingSession) {
      try {
        await axios.post(axiosPostUrl, requestBody);

        /**
         * As per Sunitha:
         * BE's setTimedAuctionLotTimes as part of the POST catalog is always holding
         * on to the previous catalogDate, either cache issue or transaction issue -
         * which needs more investigation.
         */
        if (isTimedCatalog) {
          // Make extra API call to setTimedAuctionLotTimes
          await axios(`/cps/app/catalog/${catalogID}/setTimedAuctionLotTimes`);
        }

        this.props.history.push(
          `/session/${type}/${parseInt(groupID, 10)}/${parseInt(catalogID, 10)}/6`
        );
      } catch (error) {
        this.setState({
          ...this.state,
          showSubmitError: true
        });
      }
    } else {
      // case when creating a new session
      try {
        const postResponse = await axios.post(axiosPostUrl, requestBody);
        const newCatalogID = get(postResponse, 'data.data.catalogID', null);

        // If we pass a catalogID in (Edit, not Add), we get the same one back
        if (newCatalogID) {
          await axios.post(`/cps/app/diy-step-tracker/${newCatalogID}`, 2, {
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            }
          });

          this.props.history.push(`/session/${type}/${parseInt(groupID, 10)}/${newCatalogID}/2`);
        } else {
          this.setState({
            ...this.state,
            showSubmitError: true
          });
        }
      } catch (error) {
        this.setState({
          ...this.state,
          showSubmitError: true
        });
      }
    }
  };
  /** *********************************************************
   * End Form Submit
   ********************************************************** */

  render() {
    const { loading, showShippingModal } = this.state;
    if (loading) {
      return <ReactLoading className="loading-spinner" type="spin" color="black" />;
    }
    const {
      house: { houseRef },
      location: { state },
      match: {
        params: { catalogID, groupID, type }
      }
    } = this.props;

    const {
      formErrors,
      formValues: { ampm, date, hour, location, minute, timeZone, title, isShippingEnabled },
      isTimed,
      liveToTimedEligible,
      locations,
      defaultShippingOriginID,
      resellUnsoldLiveLots,
      showModal,
      showCatalogLimitExceededError,
      showSubmitError,
      timedStaggerChecked,
      timedValue,
      timeZoneEnabled,
      shippingLocations
    } = this.state;

    /**
     * BE is inconsistent with hour values - if you set it to '12' and select
     * 'AM' the BE sets this value to '00'
     */
    const hourValue = hour === '00' ? '12' : hour;

    const isEditingSession = get(state, 'linkedFrom', null) === 'step6';

    const hasAllFields = ampm && date && hour && location && minute && timeZone && title;
    const disableContinueButton = showCatalogLimitExceededError || !hasAllFields;

    const auctionType = type.toLowerCase();

    return (
      <div className="pb-5">
        {showCatalogLimitExceededError && (
          <InlineAlert baseText={errorCatalogLimitExceededText} showPhones={false} />
        )}
        {showSubmitError && <InlineAlert baseText={errorText} />}
        <p>* = Required</p>
        <Form>
          <Row>
            <Col xs={6}>
              <FormGroup>
                <CustomInputField
                  friendlyname="Session Title"
                  hasError={formErrors.title}
                  id="title"
                  maxLength="120"
                  isRequired={true}
                  onBlur={e => this.validateRequired(e)}
                  onChange={e => this.handleTitleChange(e)}
                  value={title}
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col xs={6}>
              <FormGroup className="mb-1">
                <Label for="selectLocation">Session Location*</Label>
                <Input
                  type="select"
                  id="selectLocation"
                  name="location"
                  value={location}
                  onChange={e => this.handleSelectLocation(e)}
                >
                  {locations && locations.map((item, index) => {
                    const locationFacility = get(item, 'facilityName', '');
                    const locationStreet = get(item, 'addressStreet1', '');
                    const locationAddress = get(item, 'location', '');

                    let locationString = '';
                    if (locationFacility.length > 0) {
                      locationString += `${locationFacility} `;
                    }

                    if (locationString.length > 0) {
                      locationString += `${locationStreet}, `;
                    }

                    locationString += locationAddress;

                    if (item.primary) {
                      locationString += ' (Primary)';
                    }

                    return (
                      <option key={item.id} value={index}>
                        {locationString}
                      </option>
                    );
                  })}
                </Input>
                <Button color="link" onClick={() => this.handleLocationModal(true, -1)}>
                  <AddIcon color="primary" /> Add New Location
                </Button>
              </FormGroup>
              {isShippingEnabled && (
                <>
                  <FormGroup className="mb-1 mt-3 shipping-terms-container">
                    <Label for="selectShippingTerms" className="mb-3">
                      <Stack direction="row" spacing={1}>
                        <Chip label="NEW" color="primary" sx={{height: '22px'}}/>
                        <strong id="shippingTermsText">
                          Shipping Fulfillment Location:{' '}
                          <FontAwesomeIcon
                            className="color-blue"
                            icon="question-circle"
                          />
                        </strong>
                        <UncontrolledTooltip placement="right" target="shippingTermsText">
                          {popoverTermsText}
                        </UncontrolledTooltip>
                      </Stack>
                    </Label>
                    <Input
                      type="select"
                      name="defaultShippingOriginID"
                      id="selectShippingLocation"
                      value={this.state.formValues.defaultShippingOriginID}
                      onChange={e => this.handleShippingLocation(e)}
                    >
                      {shippingLocations
                        && shippingLocations.origins.map(origin => (
                          <option
                            key={origin.id}
                            value={origin.id}
                            disabled={origin.flag}
                          >
                            {origin.name}
                          </option>
                        ))}
                    </Input>
                  </FormGroup>
                  <Stack direction="row" spacing={1}>
                    <Button color="link" onClick={() => this.handleShippingModal(true, -1)}>
                      <AddIcon color="primary" /> Add New Location
                    </Button>
                  </Stack>

                  {showShippingModal && (
                  <ShippingLocationModal
                    isOpen={showShippingModal}
                    onClose={() => this.handleShippingModal(false)}
                    houseID={this.props.house.houseID}
                    onData={this.handleShippingModalData}
                  />
                  )}
                </>
              )}
            </Col>
          </Row>

          <FormGroup className="mt-3">
            <Row>
              <Col xs={4}>
                <Label for="date">
                  {auctionType === 'timed' ? 'First Lot End Date*' : 'Session Date*'}
                </Label>
                <FontAwesomeIcon
                  id="sessionDateTooltip"
                  className="color-blue ml-1"
                  icon="question-circle"
                />
                <UncontrolledTooltip placement="right" target="sessionDateTooltip">
                  {dateTooltipTextLine1}
                  <br />
                  {dateTooltipTextLine2}
                </UncontrolledTooltip>
                <DatePicker
                  id="date"
                  autoComplete="off"
                  className={formErrors.date ? 'form-control is-invalid' : 'form-control'}
                  minDate={minDate}
                  maxDate={maxDate}
                  placeholderText="Click to select a date"
                  selected={date}
                  onChange={e => this.handleSelectDate(e)}
                  onBlur={e => this.validateDate(e)}
                  popperPlacement="bottom-end"
                />
                {formErrors.date && <p className="color-danger my-0">{formErrors.date}</p>}
              </Col>
            </Row>
          </FormGroup>

          <FormGroup>
            <Label>{auctionType === 'timed' ? 'First Lot End Time*' : 'Session Time*'}</Label>
            <Row>
              <Col xs={2}>
                <Label className="sr-only" for="hour">
                  Hour
                </Label>
                <Input
                  className={formErrors.hour ? 'is-invalid' : ''}
                  type="select"
                  id="hour"
                  name="hour"
                  value={hourValue}
                  onBlur={e => this.validateRequired(e)}
                  onChange={e => this.handleSelectTime(e, 'hour')}
                >
                  <option value="">HH</option>
                  {timeHours.map(hourItem => (
                    <option key={hourItem} value={hourItem}>
                      {hourItem}
                    </option>
                  ))}
                </Input>
                {formErrors.hour && <p className="color-danger my-0">{formErrors.hour}</p>}
              </Col>

              <Col xs={2}>
                <Label className="sr-only" for="minute">
                  Minutes
                </Label>
                <Input
                  className={formErrors.minute ? 'is-invalid' : ''}
                  type="select"
                  id="minute"
                  name="minute"
                  value={minute}
                  onBlur={e => this.validateRequired(e)}
                  onChange={e => this.handleSelectTime(e, 'minute')}
                >
                  <option value="">MM</option>
                  {timeMinutes.map(minuteItem => (
                    <option key={minuteItem} value={minuteItem}>
                      {minuteItem}
                    </option>
                  ))}
                </Input>
                {formErrors.minute && <p className="color-danger my-0">{formErrors.minute}</p>}
              </Col>

              <Col xs={2}>
                <Label className="sr-only" for="ampm">
                  AM or PM
                </Label>
                <Input
                  className={formErrors.ampm ? 'is-invalid' : ''}
                  type="select"
                  id="ampm"
                  name="ampm"
                  value={ampm}
                  onBlur={e => this.validateRequired(e)}
                  onChange={e => this.handleSelectTime(e, 'ampm')}
                >
                  <option value="">AM/PM</option>
                  {timeAMPM.map(ampmItem => (
                    <option key={ampmItem} value={ampmItem}>
                      {ampmItem}
                    </option>
                  ))}
                </Input>
                {formErrors.ampm && <p className="color-danger my-0">{formErrors.ampm}</p>}
              </Col>
              {timeZoneEnabled && (
                <Col xs={2} className="my-2">
                  <span>{timeZone} </span>
                  <FontAwesomeIcon
                    id="timeZoneTooltip"
                    className="color-blue"
                    icon="question-circle"
                  />
                  <UncontrolledTooltip placement="right" target="timeZoneTooltip">
                    {timeZoneTooltipText}
                  </UncontrolledTooltip>
                </Col>
              )}
            </Row>
          </FormGroup>
          {auctionType === 'timed' && (
            <FormGroup className="pb-1" check>
              <Label check>
                <Input
                  type="checkbox"
                  className="mt-1 d-inline"
                  checked={timedStaggerChecked}
                  onChange={() => this.handleCheckbox()}
                />
                <p>Stagger lot end times </p>
              </Label>
              {timedStaggerChecked && (
                <>
                  <Label className="sr-only" for="stagger">
                    Stagger lot end times
                  </Label>
                  <Input
                    className="d-inline ml-3"
                    type="select"
                    id="stagger"
                    name="stagger"
                    value={timedValue}
                    onChange={e => this.handleStaggerSelection(e)}
                    style={{ width: '160px' }}
                  >
                    <option value="15">15 seconds</option>
                    <option value="30">30 seconds</option>
                    <option value="45">45 seconds</option>
                    <option value="60">1 minute</option>
                    <option value="90">1.5 minutes</option>
                    <option value="120">2 minutes</option>
                    <option value="150">2.5 minutes</option>
                  </Input>
                </>
              )}
            </FormGroup>
          )}
          {isTimed === 0 && liveToTimedEligible && (
            <FormGroup className="pb-1" check>
              <Label check>
                <Input
                  type="checkbox"
                  className="mt-1"
                  checked={resellUnsoldLiveLots}
                  onChange={this.handleReSellCheckbox}
                />
                <p>Automatically generate a timed sale from my unsold items&nbsp;</p>
              </Label>
              <FontAwesomeIcon
                id="liveToTimedTooltip"
                className="color-blue"
                icon="question-circle"
              />
              <UncontrolledTooltip placement="right" target="liveToTimedTooltip">
                {liveToTimedTooltipText}
              </UncontrolledTooltip>
            </FormGroup>
          )}
        </Form>
        <div>
          <Button
            className={`${disableContinueButton ? 'cursor-not-allowed' : ''} min-width-max-content`}
            role="link"
            color="primary"
            disabled={disableContinueButton}
            onClick={() => this.submitStepOne()}
          >
            {isEditingSession ? 'Save & Return to Summary' : 'Continue'}
          </Button>
          <Link
            className="ml-3"
            to={
              isEditingSession
                ? `/session/${type}/${parseInt(groupID, 10)}/${parseInt(catalogID, 10)}/6`
                : '/my-auctions/current'
            }
          >
            {isEditingSession ? 'Cancel & Return to Summary' : 'Cancel'}
          </Link>
        </div>
        {/**
         * numLocations is being hard coded to 1 here because if it is 0,
         * primary will be checked by default inside the modal.
         *
         * index is hard coded to -1 here because we will always be adding a
         * new location, not editing an existing one.  The values prop is null
         * for the same reason.
         *
         * enablePrimary is set to false to prevent the user from selecting a
         * location as primary when coming from DIY.
         * */}
        <EditLocation
          enablePrimary={false}
          fetchLocations={this.fetchLocations}
          handleLocationModal={this.handleLocationModal}
          houseRef={houseRef}
          index={-1}
          numLocations={1}
          showModal={showModal}
          values={null}
        />
      </div>
    );
  }
}

const mapStateToProps = ({ house, policy, user }) => ({
  house,
  policy,
  user
});

export default withRouter(
  connect(
    mapStateToProps,
    null
  )(Step1)
);
